import { CardSubscriptionPlan, Typography } from "@/components";
import type { SubscribtionPlanType } from "@/components/card/SubscriptionPlan";
import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { SUBSCRIPTION_STATUS } from "@/libraries/constant";
import { isAuthenticating } from "@/libraries/helper";
import { SubscriptionSearchSchema } from "@/schemas";
import AssistantRoundedIcon from "@mui/icons-material/AssistantRounded";
import CasesRoundedIcon from "@mui/icons-material/CasesRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";

const plans: SubscribtionPlanType[] = [
	{
		value: SUBSCRIPTION_STATUS.Free,
		title: "Free",
		description: "Get started with essential features for free",
		items: [
			{
				icon: CasesRoundedIcon,
				text: "Up to 5 new cases per month",
			},
			{
				icon: NewspaperRoundedIcon,
				text: "Follow up to 3 channels",
			},
			{
				icon: GroupsRoundedIcon,
				text: "Access to personal directory listing",
			},
			{
				icon: AssistantRoundedIcon,
				text: "Up to 10 AI questions per month",
			},
		],
	},
	{
		value: SUBSCRIPTION_STATUS.PLUS,
		title: "Plus",
		description: "Enhanced features for more productivity",
		items: [
			{
				icon: CasesRoundedIcon,
				text: "Up to 30 new cases per month",
			},
			{
				icon: NewspaperRoundedIcon,
				text: "Follow all bulletin channels",
			},
			{
				icon: GroupsRoundedIcon,
				text: "Access Pro & Verified directory listings",
			},
			{
				icon: AssistantRoundedIcon,
				text: "Up to 50 AI questions per month",
			},
		],
	},
	{
		value: SUBSCRIPTION_STATUS.PRO,
		title: "Pro",
		description: "Premium features for unlimited access",
		items: [
			{
				icon: CasesRoundedIcon,
				text: "Unlimited cases per month",
			},
			{
				icon: NewspaperRoundedIcon,
				text: "Follow and publish bulletins",
			},
			{
				icon: GroupsRoundedIcon,
				text: "Access Pro & Verified directory listings",
			},
			{
				icon: AssistantRoundedIcon,
				text: "Unlimited AI questions ",
			},
		],
	},
];

export const Route = createFileRoute("/subscription/")({
	validateSearch: zodSearchValidator(SubscriptionSearchSchema),
	beforeLoad: async () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const { profile } = useProfile({
		disableCheckExpired: true,
	});

	return (
		<>
			<BackAppBar title="Select Plan" />
			<div className="container py-2 flex flex-col gap-3 justify-center items-start">
				{plans.map((plan) => (
					<CardSubscriptionPlan
						key={plan.value}
						plan={plan}
						currentPlan={
							(profile?.subscription_status ??
								profile?.subscription_plan?.name?.toLowerCase()) as any
						}
						onClickUpgrade={(val) =>
							navigate({
								to: "/subscription/$plan",
								params: {
									plan: val,
								},
							})
						}
					/>
				))}
				<Typography fontWeight={500} fontSize={14}>
					For enterprise enquiries, please contact us at{" "}
					<Typography
						component="a"
						// @ts-expect-error
						href="mailto:contact@medplanner.io"
						className="text-primary"
					>
						contact@medplanner.io
					</Typography>
				</Typography>
			</div>
		</>
	);
}
